import { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, RefreshCw } from "lucide-react";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { NotificationItem } from "@/components/notifications/NotificationItem";
import { notificationService, CATEGORY_LABELS, emitNotificationsChanged } from "@/services/notificationService";
import { Button } from "@/components/ui/button";

const FILTERS = [["all", "All"], ["leads", "Leads"], ["tasks", "Tasks"], ["transactions", "Transactions"], ["compliance", "Compliance"], ["marketing", "Marketing"], ["business", "Business"]];

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (cat = filter) => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (cat !== "all") params.category = cat;
      setItems((await notificationService.list(params)).notifications);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const onRead = async (id) => { await notificationService.markRead(id); await load(); emitNotificationsChanged(); };
  const onDismiss = async (id) => { setItems((l) => l.filter((n) => n.id !== id)); await notificationService.dismiss(id); emitNotificationsChanged(); };
  const markAll = async () => { await notificationService.markAllRead(); await load(); emitNotificationsChanged(); };
  const refresh = async () => { setRefreshing(true); try { await notificationService.reconcile(); await load(); emitNotificationsChanged(); } finally { setRefreshing(false); } };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PageHeader eyebrow="AgentHQ · Home" title="Notifications" description="Important updates about your business, prioritized." />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="notifications-refresh">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={markAll} data-testid="notifications-page-mark-all"><CheckCheck className="h-4 w-4" /> Mark all read</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} data-testid={`notifications-filter-${val}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${filter === val ? "border-brand-navy bg-brand-navy text-white" : "border-border bg-background text-foreground hover:border-accent"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-background">
          {loading ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-16 text-center" data-testid="notifications-page-empty">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground"><Bell className="h-5 w-5" /></div>
              <p className="text-sm font-semibold text-foreground">You're all caught up.</p>
              <p className="text-xs text-muted-foreground">Important updates about your business will appear here.</p>
            </div>
          ) : (
            items.map((n) => <NotificationItem key={n.id} n={n} onRead={onRead} onDismiss={onDismiss} />)
          )}
        </div>
      </div>
    </AppShell>
  );
}
